/**
 * 同意文書の「区分」定義。
 *
 * BE は `purpose` / `data_type` / `consent_mode` の 3 つの技術フィールドを持つが、
 * 管理者にとってはこの組み合わせを判断するのが難しい。そこで FE では管理者向けに
 * 「文書の用途（4 区分）」という単一の選択肢にまとめ、内部で 3 フィールドを自動設定する
 */

import type { ConsentMode } from '../api/client';

export type ConsentDocumentCategory =
  | 'terms_of_service'
  | 'privacy_policy'
  | 'anonymous_data_use'
  | 'personal_identified_data_use';

export interface ConsentDocumentCategoryDef {
  key: ConsentDocumentCategory;
  /** 画面表示用の日本語ラベル */
  label: string;
  /** 補足説明（ラジオの下などに表示） */
  description: string;
  /** 新規作成時・逆引き時に使う BE フィールドの既定値 */
  values: {
    purpose: string;
    data_type: string;
    consent_mode: ConsentMode;
  };
  /**
   * 推奨バージョンのメジャー番号。
   * 利用規約とプライバシーポリシーは `(legacy, legacy, common)` が同じで衝突する BE 制約のため、
   * 初期のメジャーを別にして初回作成時の衝突を避ける（以降 admin がマイナー/パッチを管理）
   */
  defaultMajorVersion: number;
}

export const CONSENT_DOCUMENT_CATEGORIES: ConsentDocumentCategoryDef[] = [
  {
    key: 'terms_of_service',
    label: '利用規約',
    description: 'サービスを利用するにあたっての規約。全ユーザー（匿名モード／個人特定モード共通）で必須同意となります。',
    values: { purpose: 'legacy', data_type: 'legacy', consent_mode: 'common' },
    defaultMajorVersion: 1,
  },
  {
    key: 'privacy_policy',
    label: 'プライバシーポリシー',
    description: '個人情報の取り扱い方針。全ユーザー（匿名モード／個人特定モード共通）で必須同意となります。',
    values: { purpose: 'legacy', data_type: 'legacy', consent_mode: 'common' },
    defaultMajorVersion: 2,
  },
  {
    key: 'anonymous_data_use',
    label: '匿名データ利用',
    description: '氏名と紐付けずに集計・傾向分析するための同意。匿名モード・個人特定モード両方で必要です。',
    values: { purpose: 'anonymous_aggregate', data_type: 'voice', consent_mode: 'anonymous' },
    defaultMajorVersion: 1,
  },
  {
    key: 'personal_identified_data_use',
    label: '個人特定データ利用',
    description: '氏名・部署等と紐付けて個別フォローを行うための同意。個人特定モードのテナントでのみ必要です。',
    values: { purpose: 'personal_view', data_type: 'personal_info', consent_mode: 'personal_identified' },
    defaultMajorVersion: 1,
  },
];

/** key 指定で区分を取得 */
export function findCategoryByKey(
  key: string | null | undefined
): ConsentDocumentCategoryDef | null {
  if (!key) return null;
  return CONSENT_DOCUMENT_CATEGORIES.find((c) => c.key === key) ?? null;
}

/**
 * 既存文書の (purpose, data_type, consent_mode) から区分を逆引きする。
 * 利用規約／プライバシーポリシーはタプルが同じため、`versionMajor` で判別する
 * （利用規約 = major 1 系、プライバシーポリシー = major 2 系を既定とする）
 */
export function findCategoryByValues(doc: {
  purpose?: string;
  data_type?: string;
  consent_mode?: ConsentMode | null;
  version?: string;
}): ConsentDocumentCategoryDef | null {
  const purpose = doc.purpose ?? '';
  const dataType = doc.data_type ?? '';
  const consentMode = (doc.consent_mode ?? 'common') as ConsentMode;

  // (legacy, legacy, common) は利用規約とプライバシーポリシーで共有 → version のメジャー番号で判別
  if (purpose === 'legacy' && dataType === 'legacy' && consentMode === 'common') {
    const major = parseMajor(doc.version);
    if (major === 2) return CONSENT_DOCUMENT_CATEGORIES.find((c) => c.key === 'privacy_policy') ?? null;
    // 1.x または判別不能なら利用規約として扱う（安全側）
    return CONSENT_DOCUMENT_CATEGORIES.find((c) => c.key === 'terms_of_service') ?? null;
  }

  return (
    CONSENT_DOCUMENT_CATEGORIES.find(
      (c) =>
        c.values.purpose === purpose &&
        c.values.data_type === dataType &&
        c.values.consent_mode === consentMode
    ) ?? null
  );
}

function parseMajor(version: string | undefined): number | null {
  if (!version) return null;
  const n = Number(version.split('.')[0]);
  return Number.isFinite(n) ? n : null;
}
